import React, { useState, useMemo, useEffect } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useStaffMembers } from '../../hooks/useStaffMembers'
import {
  ListBulletIcon,
  GlobeAltIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ComputerDesktopIcon,
  UserPlusIcon,
  UsersIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CpuChipIcon,
  PlayIcon,
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline'

/* ================================================================
   VENUES — start empty until GET /api/venues (DATA_SOURCE_STRATEGY §3.3)
   ================================================================ */
function toCanonicalVenueId(venueId) {
  if (!venueId) return venueId
  const s = String(venueId).toLowerCase()
  if (s === 'diggers' || s.endsWith('diggers')) return 'diggers'
  if (s === 'leagues' || s.endsWith('leagues')) return 'leagues'
  if (s === 'golf' || s.endsWith('golf')) return 'golf'
  if (s === 'sports' || s.endsWith('sports')) return 'sports'
  return s
}

function useVenues() {
  const [venues, setVenues] = useState([])
  useEffect(() => {
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && j.data.length > 0) {
          setVenues(
            j.data.map((v) => ({
              id: toCanonicalVenueId(v.venue_id),
              name: v.name,
              shortName: v.code || v.name?.split(' ')[0] || v.venue_id,
            }))
          )
        }
      } catch {}
    })()
  }, [])
  return venues
}

/* ================================================================
   TEMPLATES
   ================================================================ */
const TEMPLATES = [
  {
    id: 'T01',
    name: 'New EGM Added',
    icon: ComputerDesktopIcon,
    color: '#6366f1',
    trigger: 'When a new EGM is installed or replaced on the gaming floor',
    usedCount: 2,
    items: [
      { desc: 'Record EGM details in Operations Safe (make, model, serial, position)', required: true, evidence: false },
      { desc: 'Configure TITO settings if applicable', required: false, evidence: false },
      { desc: 'Verify meter reading integration for new machines', required: true, evidence: true },
      { desc: 'Update risk assessment if EGM count changes risk profile', required: true, evidence: true },
      { desc: 'Review TMP rules — any new rules needed for expanded floor?', required: true, evidence: false },
      { desc: 'Notify AUSTRAC if entitlement count crossed reporting threshold', required: true, evidence: true },
    ],
  },
  {
    id: 'T02',
    name: 'Staff Onboarded',
    icon: UserPlusIcon,
    color: '#059669',
    trigger: 'When a new staff member starts in a gaming or compliance role',
    usedCount: 3,
    items: [
      { desc: 'Conduct AML/CTF induction training', required: true, evidence: true },
      { desc: 'Issue staff handbook with AML/CTF procedures section', required: true, evidence: false },
      { desc: 'Set up system access with appropriate role permissions', required: true, evidence: false },
      { desc: 'Conduct employee due diligence screening', required: true, evidence: true },
      { desc: 'Assign to gaming floor roster and brief on observation duties', required: false, evidence: false },
    ],
  },
  {
    id: 'T03',
    name: 'Staff Offboarded',
    icon: UsersIcon,
    color: '#64748b',
    trigger: 'When a gaming or compliance staff member leaves the venue',
    usedCount: 1,
    items: [
      { desc: 'Revoke system access and change shared credentials', required: true, evidence: false },
      { desc: 'Reassign open cases, alerts, and tasks', required: true, evidence: false },
      { desc: 'Final compliance review sign-off', required: true, evidence: true },
      { desc: 'Update staffing records and training register', required: true, evidence: false },
    ],
  },
  {
    id: 'T04',
    name: 'Quarterly Compliance Review',
    icon: CalendarDaysIcon,
    color: '#ca8a04',
    trigger: 'Every quarter — scheduled compliance posture review',
    usedCount: 5,
    items: [
      { desc: 'Review venue risk assessment — any trigger events or changes?', required: true, evidence: true },
      { desc: 'Review regulatory obligations register — any overdue or partial items?', required: true, evidence: false },
      { desc: 'Review rules engine performance and false positive rate', required: true, evidence: true },
      { desc: 'Review staff training status — any overdue refreshers?', required: true, evidence: false },
      { desc: 'Review open alerts and SMR pipeline', required: true, evidence: false },
      { desc: 'Review ECDD active cases — any requiring escalation?', required: true, evidence: false },
      { desc: 'Prepare Board compliance report', required: true, evidence: true },
    ],
  },
  {
    id: 'T05',
    name: 'Annual Program Review',
    icon: DocumentTextIcon,
    color: '#8b5cf6',
    trigger: 'Annual review of AML/CTF program — Part A and Part B',
    usedCount: 1,
    items: [
      { desc: 'Review and update ML/TF risk assessment', required: true, evidence: true },
      { desc: 'Review AML/CTF program against current AUSTRAC guidance', required: true, evidence: true },
      { desc: 'Review all 11 mandatory program components for completeness', required: true, evidence: false },
      { desc: 'Assess whether independent review is due', required: true, evidence: false },
      { desc: 'Update program document and submit for Board approval', required: true, evidence: true },
      { desc: 'Submit annual compliance report to AUSTRAC (if due Jan-Mar)', required: true, evidence: true },
    ],
  },
  {
    id: 'T06',
    name: 'Technology Change',
    icon: CpuChipIcon,
    color: '#f59e0b',
    trigger: 'When TITO rollout, cashless gaming, or system upgrade occurs',
    usedCount: 1,
    items: [
      { desc: 'Document the technology change and scope of impact', required: true, evidence: true },
      { desc: 'Update venue risk assessment — technology dimension', required: true, evidence: true },
      { desc: 'Review and update TMP rules for new technology', required: true, evidence: false },
      { desc: 'Conduct staff training on new technology and AML implications', required: true, evidence: true },
      { desc: 'Update AML/CTF program if procedures have changed', required: true, evidence: false },
    ],
  },
]

const CL_STATUS = {
  in_progress: { label: 'In Progress', color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  completed: { label: 'Completed', color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
  overdue: { label: 'Overdue', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20' },
  not_started: { label: 'Not Started', color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-800' },
  cancelled: { label: 'Cancelled', color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-800' },
}


/* ================================================================
   INSTANCE CARD
   ================================================================ */
function InstanceCard({ checklist, isExpanded, onToggle, onCompleteItem, venues }) {
  const statusCfg = CL_STATUS[checklist.status] || CL_STATUS.not_started
  const venue = venues.find((v) => v.id === checklist.venueId)
  const progress = checklist.items.filter((i) => i.isCompleted).length
  const total = checklist.items.length
  const pct = total > 0 ? Math.round((progress / total) * 100) : 0

  return (
    <div className="card overflow-hidden">
      <div onClick={onToggle} className="px-3.5 py-2.5 cursor-pointer">
        <div className="flex gap-2 items-center">
          {isExpanded ? (
            <ChevronDownIcon className="w-4 h-4 text-psg-teal flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
          <ListBulletIcon className="w-4 h-4 text-indigo-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex gap-1.5 items-center flex-wrap">
              <span className="text-[14px] font-bold text-gray-900 dark:text-gray-100">{checklist.name}</span>
              <span className={`text-[12px] font-semibold px-1.5 py-0.5 rounded ${statusCfg.bg} ${statusCfg.color}`}>
                {statusCfg.label}
              </span>
            </div>
            <div className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5">
              {venue?.shortName || venue?.name || checklist.venueId} · Due: {checklist.dueDate} · {checklist.assignedTo}
            </div>
          </div>
          <div className="text-right min-w-[60px] flex-shrink-0">
            <div className={`text-[14px] font-bold ${pct === 100 ? 'text-green-600' : 'text-psg-teal dark:text-psg-teal-300'}`}>
              {progress}/{total}
            </div>
            <div className="w-[60px] h-1 rounded-full bg-gray-200 dark:bg-gray-700 mt-0.5">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#16a34a' : 'var(--psg-teal)' }}
              />
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-3.5 pb-3 pt-0 border-t border-gray-100 dark:border-gray-700">
          {checklist.items.map((item, i) => (
            <div
              key={item.id}
              className="flex gap-3 items-start py-2 pl-5 border-b border-gray-50 dark:border-gray-700/50 last:border-0"
            >
              <div className="flex-shrink-0 pt-0.5">
                {item.isCompleted ? (
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                ) : (
                  <div className="w-6 h-6 rounded border-2 border-gray-200 dark:border-gray-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className={`text-[14px] ${item.isCompleted ? 'line-through text-gray-600 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}
                >
                  {item.description}
                  {item.isRequired && <span className="text-red-600 dark:text-red-400 font-bold ml-0.5" title="Required">*</span>}
                </div>
                <div className="flex gap-1.5 mt-1 items-center">
                  {item.evidence && !item.isCompleted && (
                    <>
                      <input
                        id={`file-${item.id}`}
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={(e) => { 
                          const file = e.target.files?.[0]
                          if (file) onCompleteItem?.(checklist.id, item.id, file)
                          e.target.value = ''
                        }}
                      />
                      <label htmlFor={`file-${item.id}`} className="p-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900/50" title="Upload evidence">
                        <ArrowUpTrayIcon className="w-4 h-4" />
                      </label>
                    </>
                  )}
                  {item.completedBy && (
                    <span className="text-[14px] text-gray-600 dark:text-gray-500">{item.completedBy} · {item.completedAt}</span>
                  )}
                </div>
              </div>
              {!item.isCompleted && (
                item.evidence ? (
                  <label htmlFor={`file-${item.id}`} className="text-[14px] font-semibold px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 cursor-pointer flex-shrink-0 flex items-center gap-1">
                    <ArrowUpTrayIcon className="w-4 h-4" /> Upload document
                  </label>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onCompleteItem?.(checklist.id, item.id) }}
                    className="text-[14px] font-semibold px-2 py-1 rounded bg-psg-teal/10 text-psg-teal hover:bg-psg-teal/20 flex-shrink-0"
                  >
                    Complete
                  </button>
                )
              )}
            </div>
          ))}
          {checklist.completedAt && (
            <div className="text-[12px] text-green-600 dark:text-green-400 mt-1.5 flex items-center gap-1">
              <CheckCircleIcon className="w-4 h-4" /> Completed {checklist.completedAt}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ================================================================
   USE CHECKLIST MODAL
   ================================================================ */
function UseChecklistModal({ template, venues, staffOptions = [], onStartChecklist, onClose }) {
  const Icon = template.icon
  const today = new Date().toISOString().slice(0, 10)
  const [title, setTitle] = useState('')
  const [assignee, setAssignee] = useState(staffOptions[0]?.label || '')
  const [dueDate, setDueDate] = useState('')
  const [notes, setNotes] = useState('')
  const [venueId, setVenueId] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!venues.length) return
    setVenueId((prev) => (prev && venues.some((v) => v.id === prev) ? prev : venues[0].id))
  }, [venues])

  useEffect(() => {
    if (staffOptions.length === 0) {
      setAssignee('')
      return
    }
    setAssignee((prev) => {
      const stillValid = staffOptions.some((o) => o.label === prev)
      return stillValid ? prev : staffOptions[0].label
    })
  }, [staffOptions])

  const handleStart = async () => {
    const id = `CL-${Date.now()}`
    const newChecklist = {
      id,
      venueId,
      name: title.trim() || template.name,
      type: template.id,
      status: 'in_progress',
      progress: 0,
      total: template.items.length,
      dueDate: dueDate || today,
      assignedTo: assignee,
      createdAt: today,
      completedAt: null,
      triggerEntity: null,
      items: template.items.map((item, i) => ({
        id: `${id}-${i + 1}`,
        description: item.desc,
        isRequired: item.required,
        evidence: item.evidence,
        isCompleted: false,
        completedAt: null,
        completedBy: null,
        notes: null,
      })),
    }
    setSaving(true)
    await onStartChecklist(newChecklist)
    setSaving(false)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg max-h-[85vh] overflow-auto p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2 items-center">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${template.color}20` }}>
              <Icon className="w-5 h-5" style={{ color: template.color }} />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Use: {template.name}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <p className="text-[12px] text-gray-500 dark:text-gray-400 mb-4">{template.trigger}</p>

        <div className="mb-3">
          <label className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Title / Context</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Q3 2025 Quarterly Review, Chris Martin — Gaming Attendant"
            className="input-field text-[14px]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Venue</label>
            <select value={venueId} onChange={(e) => setVenueId(e.target.value)} disabled={venues.length === 0} className="select-field text-[14px]">
              {venues.length === 0 ? (
                <option value="">No venues yet — check Admin / venues API</option>
              ) : (
                venues.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))
              )}
            </select>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Assigned To</label>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="select-field text-[14px]">
              {staffOptions.length === 0 ? (
                <option value="">No staff in directory — add in Admin → Employees</option>
              ) : (
                staffOptions.map((opt) => (
                  <option key={opt.value} value={opt.label}>{opt.label}</option>
                ))
              )}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Due Date</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="input-field text-[14px]" />
          </div>
        </div>
        <div className="mb-4">
          <label className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 uppercase block mb-1">Notes (optional)</label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any context about what triggered this checklist..."
            className="input-field text-[14px] resize-none"
          />
        </div>

        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 mb-4">
          <div className="text-[14px] font-semibold text-psg-teal dark:text-psg-teal-300 mb-2">
            {template.items.length} checklist items:
          </div>
          {template.items.map((item, i) => (
            <div key={i} className="flex gap-2 items-start py-2 pl-5 border-b border-gray-100 dark:border-gray-600 last:border-0">
              <div className="w-6 h-6 rounded border-2 border-gray-200 dark:border-gray-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0 text-[14px] text-gray-800 dark:text-gray-200">
                {item.desc}
                {item.required && <span className="text-red-600 dark:text-red-400 font-bold ml-0.5" title="Required">*</span>}
              </div>
              {item.evidence && (
                <span className="p-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 flex-shrink-0" title="Evidence required">
                  <ArrowUpTrayIcon className="w-4 h-4" />
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="button" onClick={handleStart} className="btn-primary flex items-center gap-1.5" disabled={saving || venues.length === 0 || !venueId}>
            <PlayIcon className="w-4 h-4" /> {saving ? 'Saving…' : 'Start Checklist'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ================================================================
   MANAGE TEMPLATES MODAL
   ================================================================ */
function ManageTemplatesModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-xl max-h-[85vh] overflow-auto p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Manage Checklist Templates</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <p className="text-[12px] text-gray-500 dark:text-gray-400 mb-4">
          Define checklist templates for governance events. Each template contains a set of items that must be completed when the event occurs.
        </p>

        {TEMPLATES.map((t) => {
          const Icon = t.icon
          return (
            <div key={t.id} className="flex gap-2.5 items-center p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 mb-1">
              <Icon className="w-4 h-4 flex-shrink-0" style={{ color: t.color }} />
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">{t.name}</div>
                <div className="text-[12px] text-gray-500 dark:text-gray-400">{t.items.length} items · Used {t.usedCount} time{t.usedCount !== 1 ? 's' : ''}</div>
              </div>
              <button type="button" className="p-1 text-gray-400 hover:text-gray-600"><PencilSquareIcon className="w-4 h-4" /></button>
              {t.usedCount === 0 && <button type="button" className="p-1 text-gray-400 hover:text-gray-500"><TrashIcon className="w-4 h-4" /></button>}
            </div>
          )
        })}

        <button
          type="button"
          className="mt-2.5 text-[12px] font-semibold px-3 py-2 rounded-lg border border-psg-teal/30 bg-psg-teal/10 text-psg-teal hover:bg-psg-teal/20 flex items-center gap-1"
        >
          <span className="font-bold">+</span> Add Template
        </button>

        <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-[12px] text-amber-800 dark:text-amber-200 leading-relaxed mt-2.5">
          Templates that have been used cannot be deleted — they're part of your audit trail. You can deactivate them instead.
        </div>

        <div className="flex gap-2 justify-end mt-4">
          <button type="button" onClick={onClose} className="btn-primary">Done</button>
        </div>
      </div>
    </div>
  )
}

/* ================================================================
   MAIN
   ================================================================ */
export default function ComplianceChecklist() {
  const venues = useVenues()
  const { assigneeOptions: staffOptions } = useStaffMembers()
  const [checklists, setChecklists] = useState([])
  const [venueFilter, setVenueFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [expandedChecklist, setExpandedChecklist] = useState(null)
  const [useTemplate, setUseTemplate] = useState(null)
  const [showManage, setShowManage] = useState(false)
  const [showCompleted, setShowCompleted] = useState(false)

  // Load checklists from API (persisted from governance_checklist_batches + items)
  useEffect(() => {
    const load = async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/governance/checklist/instances'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && j.data.length > 0) {
          setChecklists(j.data.map(o => ({
            id: o.id,
            venueId: o.venueId || '',
            name: o.name || '',
            type: o.type || 'checklist',
            status: o.status || 'not_started',
            progress: o.progress || 0,
            total: o.total || 0,
            dueDate: o.dueDate,
            assignedTo: o.assignedTo || '',
            createdAt: o.createdAt,
            completedAt: o.completedAt,
            triggerEntity: null,
            items: (o.items || []).map(i => ({
              id: i.id,
              description: i.description,
              isRequired: !!i.isRequired,
              evidence: !!i.evidence,
              isCompleted: !!i.isCompleted,
              completedAt: i.completedAt,
              completedBy: i.completedBy,
              notes: i.notes
            }))
          })))
        }
      } catch (_) {}
    }
    load()
  }, [])

  const onStartChecklist = async (newChecklist) => {
    try {
      const r = await authFetch(getLocalApiUrl('/api/governance/checklist/from-template'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venueId: newChecklist.venueId,
          name: newChecklist.name,
          type: newChecklist.type,
          dueDate: newChecklist.dueDate,
          assignedTo: newChecklist.assignedTo,
          items: newChecklist.items.map((i) => ({
            description: i.description,
            isRequired: i.isRequired,
            evidence: i.evidence,
          })),
        }),
      })
      const j = await r.json()
      if (j.success && j.data) {
        const saved = j.data
        setChecklists((prev) => [saved, ...prev])
        setExpandedChecklist(saved.id)
      } else {
        setChecklists((prev) => [newChecklist, ...prev])
        setExpandedChecklist(newChecklist.id)
      }
    } catch (_) {
      setChecklists((prev) => [newChecklist, ...prev])
      setExpandedChecklist(newChecklist.id)
    }
  }

  const onCompleteItem = async (checklistId, itemId, file = null) => {
    try {
      if (file) {
        const fd = new FormData()
        fd.append('file', file)

        const uploadRes = await authFetch(
          getLocalApiUrl(`/api/governance/checklist/items/${itemId}/evidence`),
          { method: 'POST', body: fd }
        )
        const uploadJson = await uploadRes.json().catch(() => ({}))
        if (!uploadRes.ok || !uploadJson.success) {
          throw new Error(uploadJson.error || 'Upload failed')
        }
      } else {
        const r = await authFetch(getLocalApiUrl(`/api/governance/checklist/items/${itemId}/complete`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ completedBy: checklists.find((c) => c.id === checklistId)?.assignedTo }),
        })
        const j = await r.json().catch(() => ({}))
        if (!r.ok || !j.success) throw new Error(j.error || 'Failed to save')
      }
    } catch (err) {
      console.error('Complete item failed:', err)
      alert(err.message || 'Failed to complete item')
      return
    }

    setChecklists((prev) =>
      prev.map((c) => {
        if (c.id !== checklistId) return c
        const items = c.items.map((i) =>
          i.id === itemId
            ? { ...i, isCompleted: true, completedAt: new Date().toISOString().slice(0, 10), completedBy: c.assignedTo }
            : i
        )
        const allDone = items.every((i) => i.isCompleted)
        return {
          ...c,
          items,
          progress: items.filter((i) => i.isCompleted).length,
          ...(allDone ? { status: 'completed', completedAt: new Date().toISOString().slice(0, 10) } : {}),
        }
      })
    )
  }

  const filteredChecklists = useMemo(() => {
    return checklists.filter((c) => {
      if (venueFilter !== 'all' && c.venueId !== venueFilter) return false
      if (search) {
        const q = search.toLowerCase()
        if (!c.name.toLowerCase().includes(q) && !c.assignedTo.toLowerCase().includes(q)) return false
      }
      return true
    })
  }, [checklists, venueFilter, search])

  const activeChecklists = filteredChecklists.filter((c) => c.status !== 'completed' && c.status !== 'cancelled')
  const completedChecklists = filteredChecklists.filter((c) => c.status === 'completed')

  const stats = useMemo(
    () => ({
      templates: TEMPLATES.length,
      active: activeChecklists.length,
      completed: completedChecklists.length,
      total: filteredChecklists.length,
    }),
    [filteredChecklists, activeChecklists, completedChecklists]
  )

  return (
    <div className="max-w-7xl mx-auto pb-10 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex gap-2 items-center">
            <ListBulletIcon className="w-6 h-6 text-psg-teal" />
            Governance Checklists
          </h1>
          <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-1">
            Compliance procedure templates — use when governance events occur
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <GlobeAltIcon className="w-4 h-4 text-gray-500" />
            <select
              value={venueFilter}
              onChange={(e) => setVenueFilter(e.target.value)}
              className="select-field w-auto min-w-[160px] text-[14px]"
            >
              <option value="all">All Venues</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
          <button 
            type="button"
            onClick={() => setShowManage(true)}
            className="btn-secondary text-[14px] py-2 flex gap-1.5 items-center"
          >
            <Cog6ToothIcon className="w-4 h-4" /> Manage Templates
          </button>
        </div>
      </div>

      {/* Status bar */}
      <div className="card flex flex-col sm:flex-row overflow-hidden p-0">
        <div className="flex-1 px-5 py-3">
          <div className="text-[12px] font-semibold text-gray-400 uppercase">Templates</div>
          <div className="text-[22px] font-extrabold leading-none mt-1 text-psg-teal dark:text-psg-teal-300">{stats.templates}</div>
          <div className="text-[12px] text-gray-500 dark:text-gray-400">defined</div>
        </div>
        <div className="px-5 py-3 border-t sm:border-t-0 sm:border-l border-gray-200 dark:border-gray-700">
          <div className="text-[12px] font-semibold text-gray-400 uppercase">Active Now</div>
          <div className={`text-[22px] font-extrabold leading-none mt-1 ${stats.active > 0 ? 'text-amber-600' : 'text-green-600'}`}>
            {stats.active}
          </div>
          <div className="text-[12px] text-gray-500 dark:text-gray-400">in progress</div>
        </div>
        <div className="px-5 py-3 border-t sm:border-t-0 sm:border-l border-gray-200 dark:border-gray-700">
          <div className="text-[12px] font-semibold text-gray-400 uppercase">Completed</div>
          <div className="text-[22px] font-extrabold leading-none mt-1 text-green-600">{stats.completed}</div>
          <div className="text-[12px] text-gray-500 dark:text-gray-400">all time</div>
        </div>
        <div className="px-5 py-3 border-t sm:border-t-0 sm:border-l border-gray-200 dark:border-gray-700">
          <div className="text-[12px] font-semibold text-gray-400 uppercase">Total Runs</div>
          <div className="text-[22px] font-extrabold leading-none mt-1 text-psg-teal dark:text-psg-teal-300">{stats.total}</div>
          <div className="text-[12px] text-gray-500 dark:text-gray-400">since inception</div>
        </div>
      </div>

      {/* Template Library */}
      <div>
        <div className="text-[14px] font-bold mb-2 text-psg-teal dark:text-psg-teal-300">Checklist Templates</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {TEMPLATES.map((t) => {
            const Icon = t.icon
            return (
              <div key={t.id} className="card p-3 flex gap-2.5 items-start">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${t.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: t.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-bold text-gray-900 dark:text-gray-100">{t.name}</div>
                  <div className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{t.trigger}</div>
                  <div className="flex gap-2 mt-1 text-[12px] text-gray-400 flex-wrap">
                    <span>{t.items.length} items</span>
                    <span>·</span>
                    <span>Used {t.usedCount}×</span>
                    {t.items.filter((i) => i.evidence).length > 0 && (
                      <>
                        <span>·</span>
                        <span className="text-amber-600">{t.items.filter((i) => i.evidence).length} need evidence</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setUseTemplate(t)}
                  className="btn-primary text-[12px] py-1.5 px-3 flex gap-1 items-center flex-shrink-0"
                >
                  <PlayIcon className="w-4 h-4" /> Use
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Active Checklists */}
      {activeChecklists.length > 0 && (
        <div>
          <div className="text-[14px] font-bold mb-2 flex items-center gap-1.5 text-psg-teal dark:text-psg-teal-300">
            <ClockIcon className="w-4 h-4 text-amber-600" /> Active Checklists
            <span className="font-normal text-gray-400">({activeChecklists.length})</span>
          </div>
          <div className="space-y-2">
            {activeChecklists.map((checklist) => (
              <InstanceCard
                key={checklist.id}
                checklist={checklist}
                isExpanded={expandedChecklist === checklist.id}
                onToggle={() => setExpandedChecklist(expandedChecklist === checklist.id ? null : checklist.id)}
                onCompleteItem={onCompleteItem}
                venues={venues}
              />
            ))}
          </div>
        </div>
      )}

      {/* Completed Checklists */}
      <div>
        <div
          onClick={() => setShowCompleted(!showCompleted)}
          className="text-[14px] font-bold mb-2 flex items-center gap-1.5 cursor-pointer text-psg-teal dark:text-psg-teal-300"
        >
          {showCompleted ? (
            <ChevronDownIcon className="w-4 h-4 text-green-600" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-gray-400" />
          )}
          <CheckCircleIcon className="w-4 h-4 text-green-600" /> Completed
          <span className="font-normal text-gray-400">({completedChecklists.length})</span>
        </div>
        {showCompleted && (
          <div className="space-y-2">
            {completedChecklists.map((checklist) => (
              <InstanceCard
                key={checklist.id}
                checklist={checklist}
                isExpanded={expandedChecklist === checklist.id}
                onToggle={() => setExpandedChecklist(expandedChecklist === checklist.id ? null : checklist.id)}
                onCompleteItem={onCompleteItem}
                venues={venues}
              />
            ))}
          </div>
        )}
      </div>

      <div className="py-2.5 text-[12px] text-gray-500 dark:text-gray-400">
        Templates define your governance procedures. Each use creates an auditable record of compliance actions taken.
      </div>

      {useTemplate && (
        <UseChecklistModal
          template={useTemplate}
          venues={venues}
          staffOptions={staffOptions}
          onStartChecklist={onStartChecklist}
          onClose={() => setUseTemplate(null)}
        />
      )}
      {showManage && <ManageTemplatesModal onClose={() => setShowManage(false)} />}
    </div>
  )
}
